import { getLogger } from '@packages/core/logging';
import {
    type FailCodeEntity,
    type FailCodeFigureEntity,
    type FailCodeSectionEntity,
    type FailCodeSubsectionEntity,
    FetchSectionsByVehicle,
    LookupFailCodes,
    LookupFigures,
    LookupSubsections,
} from '@packages/domain/fail-codes';
import { container } from '@web/services/container';
import { useCallback, useEffect, useState } from 'react';

import type { FailCodeSelection, FailCodeVehicleParams } from '../fail-codes-modal.types';

const logger = getLogger('techline');

/**
 * Presentation-layer option shape for Typeahead dropdowns.
 * Kept in this hook file because it is a UI concern, not a domain type.
 */
export type SelectOption = {
    /** The display text shown to the user. */
    label: string;

    /** The underlying value used for lookups and submissions. */
    value: string;
};

// Operations are prefixed with $ to make them visually distinct in this file.
const [$fetchSectionsByVehicle, $lookupFailCodes, $lookupFigures, $lookupSubsections] = container.resolveOperations(
    FetchSectionsByVehicle,
    LookupFailCodes,
    LookupFigures,
    LookupSubsections
);

/**
 * Manages the 4-level cascading lookup state machine for the "Lookup by Section" fail code search
 * method: section -> subsection -> figure/repair-type -> fail code. No auto-selection occurs; the
 * user must explicitly choose a value at each level.
 *
 * When the user selects a value at level N, all downstream levels (N+1, N+2, ...) are cleared and
 * options for level N+1 are fetched. The hook returns selection state, option arrays, handlers, and
 * a `confirmSelection` function that builds a {@link FailCodeSelection}.
 *
 * @param vehicleParams - the vehicle identification parameters scoping the lookup
 * @returns hook state and handlers for the 4-level cascading section lookup
 */
export function useLookupBySection(vehicleParams: FailCodeVehicleParams) {
    // State
    const [failCodeOptionData, setFailCodeOptionData] = useState<SelectOption[]>([]);
    const [figureOptionData, setFigureOptionData] = useState<SelectOption[]>([]);
    const [isConfirming, setIsConfirming] = useState(false);
    const [sectionOptionData, setSectionOptionData] = useState<SelectOption[]>([]);
    const [selectedFailCode, setSelectedFailCode] = useState<SelectOption[]>([]);
    const [selectedFigure, setSelectedFigure] = useState<SelectOption[]>([]);
    const [selectedSection, setSelectedSection] = useState<SelectOption[]>([]);
    const [selectedSubsection, setSelectedSubsection] = useState<SelectOption[]>([]);
    const [subsectionOptionData, setSubsectionOptionData] = useState<SelectOption[]>([]);

    // Derived state
    const isConfirmDisabled =
        selectedSection.length === 0 ||
        selectedSubsection.length === 0 ||
        selectedFigure.length === 0 ||
        selectedFailCode.length === 0;

    // Handlers

    /**
     * Handle the user selecting a section from the first-level Typeahead. Clears all downstream
     * selections and fetches subsections for the chosen section.
     *
     * @param selected - the selected section option(s) from the Typeahead
     */
    const handleSectionChange = useCallback(
        async (selected: SelectOption[]) => {
            // Update section selection and clear all downstream state
            setSelectedSection(selected);
            setSelectedSubsection([]);
            setSubsectionOptionData([]);
            setSelectedFigure([]);
            setFigureOptionData([]);
            setSelectedFailCode([]);
            setFailCodeOptionData([]);

            const section = selected[0];
            if (!section) {
                return;
            }

            // Fetch subsections for the selected section
            try {
                const subsections = await $lookupSubsections({
                    modelCode: vehicleParams.modelCode,
                    modelYear: vehicleParams.modelYear,
                    optionCode: vehicleParams.optionCode,
                    sectionNumber: Number(section.value),
                    sectionName: section.label,
                });

                const options = mapSubsectionsToOptions(subsections);
                setSubsectionOptionData(options);
            } catch (error) {
                logger.error('Failed to fetch subsections', { error });
            }
        },
        [vehicleParams.modelCode, vehicleParams.modelYear, vehicleParams.optionCode]
    );

    /**
     * Handle the user selecting a subsection from the second-level Typeahead. Clears figure and
     * fail code selections and fetches figures for the chosen section + subsection.
     *
     * @param selected - the selected subsection option(s) from the Typeahead
     */
    const handleSubsectionChange = useCallback(
        async (selected: SelectOption[]) => {
            // Update subsection selection and clear downstream state
            setSelectedSubsection(selected);
            setSelectedFigure([]);
            setFigureOptionData([]);
            setSelectedFailCode([]);
            setFailCodeOptionData([]);

            const subsection = selected[0];
            const section = selectedSection[0];
            if (!subsection || !section) {
                return;
            }

            // Fetch figures for the selected section and subsection
            try {
                const figures = await $lookupFigures({
                    modelCode: vehicleParams.modelCode,
                    modelYear: vehicleParams.modelYear,
                    optionCode: vehicleParams.optionCode,
                    sectionNumber: Number(section.value),
                    sectionName: section.label,
                    subsectionNumber: Number(subsection.value),
                    subsectionName: subsection.label,
                });

                const options = mapFiguresToOptions(figures);
                setFigureOptionData(options);
            } catch (error) {
                logger.error('Failed to fetch figures', { error });
            }
        },
        [selectedSection, vehicleParams.modelCode, vehicleParams.modelYear, vehicleParams.optionCode]
    );

    /**
     * Handle the user selecting a figure/repair-type from the third-level Typeahead. Clears the
     * fail code selection and fetches fail codes for the chosen section + subsection + figure.
     *
     * @param selected - the selected figure option(s) from the Typeahead
     */
    const handleFigureChange = useCallback(
        async (selected: SelectOption[]) => {
            // Update figure selection and clear fail code state
            setSelectedFigure(selected);
            setSelectedFailCode([]);
            setFailCodeOptionData([]);

            const figure = selected[0];
            const section = selectedSection[0];
            const subsection = selectedSubsection[0];
            if (!figure || !section || !subsection) {
                return;
            }

            // Fetch fail codes for the selected section, subsection, and figure
            try {
                const failCodes = await $lookupFailCodes({
                    modelCode: vehicleParams.modelCode,
                    modelYear: vehicleParams.modelYear,
                    optionCode: vehicleParams.optionCode,
                    sectionNumber: Number(section.value),
                    subsectionNumber: Number(subsection.value),
                    figureCode: figure.value,
                });

                const options = mapFailCodesToOptions(failCodes);
                setFailCodeOptionData(options);
            } catch (error) {
                logger.error('Failed to fetch fail codes', { error });
            }
        },
        [
            selectedSection,
            selectedSubsection,
            vehicleParams.modelCode,
            vehicleParams.modelYear,
            vehicleParams.optionCode,
        ]
    );

    /**
     * Handle the user selecting a fail code from the fourth-level Typeahead.
     *
     * @param selected - the selected fail code option(s) from the Typeahead
     */
    const handleFailCodeChange = useCallback((selected: SelectOption[]) => {
        setSelectedFailCode(selected);
    }, []);

    /**
     * Build and return the final {@link FailCodeSelection} from the current selections, then
     * set the confirming flag while the parent processes the result.
     *
     * @returns the confirmed fail code selection, or `null` if a selection is incomplete
     */
    const confirmSelection = useCallback((): FailCodeSelection | null => {
        if (isConfirming) {
            return null;
        }

        const failCode = selectedFailCode[0]?.value;
        const sectionName = selectedSection[0]?.label;
        const subsectionName = selectedSubsection[0]?.label;

        if (!failCode) {
            return null;
        }

        setIsConfirming(true);

        return {
            failCode,
            repairCode: failCode,
            section: sectionName,
            subSection: subsectionName,
        };
    }, [isConfirming, selectedFailCode, selectedSection, selectedSubsection]);

    // Effects

    /**
     * Fetch initial section options when the component mounts or vehicle params change.
     */
    useEffect(() => {
        /** Fetch sections from the API and populate the first-level typeahead options. */
        async function fetchSections() {
            try {
                const sections = await $fetchSectionsByVehicle({
                    modelCode: vehicleParams.modelCode,
                    modelYear: vehicleParams.modelYear,
                    optionCode: vehicleParams.optionCode,
                });

                const options = mapSectionsToOptions(sections);
                setSectionOptionData(options);
            } catch (error) {
                logger.error('Failed to fetch sections', { error });
            }
        }

        fetchSections();
    }, [vehicleParams.modelCode, vehicleParams.modelYear, vehicleParams.optionCode]);

    return {
        /**
         * Build and return the final fail code selection from current state.
         * Returns `null` if a confirmation is already in progress or selections are incomplete.
         */
        confirmSelection,

        /** Options for the fail code (fourth-level) Typeahead. */
        failCodeOptionData,

        /** Options for the figure/repair-type (third-level) Typeahead. */
        figureOptionData,

        /** Handler for fail code Typeahead selection changes. */
        handleFailCodeChange,

        /** Handler for figure/repair-type Typeahead selection changes. */
        handleFigureChange,

        /** Handler for section Typeahead selection changes. */
        handleSectionChange,

        /** Handler for subsection Typeahead selection changes. */
        handleSubsectionChange,

        /** Whether the confirm button should be disabled (incomplete selections). */
        isConfirmDisabled,

        /** Whether a confirmation is currently being processed. */
        isConfirming,

        /** Options for the section (first-level) Typeahead. */
        sectionOptionData,

        /** The currently selected fail code option(s). */
        selectedFailCode,

        /** The currently selected figure/repair-type option(s). */
        selectedFigure,

        /** The currently selected section option(s). */
        selectedSection,

        /** The currently selected subsection option(s). */
        selectedSubsection,

        /** Options for the subsection (second-level) Typeahead. */
        subsectionOptionData,
    };
}

/**
 * Map API fail code objects to Typeahead-compatible select options. The label combines the
 * fail code and description for easier identification.
 *
 * @param failCodes - the fail codes returned by the API
 * @returns an array of select options
 * @example
 * ```ts
 * const options = mapFailCodesToOptions([{ failCode: 'FC01', description: 'Sensor failure' }]);
 * // [{ label: 'FC01 - Sensor failure', value: 'FC01' }]
 * ```
 */
function mapFailCodesToOptions(failCodes: FailCodeEntity[]): SelectOption[] {
    return failCodes.map((f) => ({ label: `${f.failCode} - ${f.description ?? ''}`, value: f.failCode }));
}

/**
 * Map API figure objects to Typeahead-compatible select options. The label combines the
 * figure code and description for easier identification.
 *
 * @param figures - the figures returned by the API
 * @returns an array of select options
 * @example
 * ```ts
 * const options = mapFiguresToOptions([{ figureCode: 'A1', figureDescription: 'Front Panel' }]);
 * // [{ label: 'A1 - Front Panel', value: 'A1' }]
 * ```
 */
function mapFiguresToOptions(figures: FailCodeFigureEntity[]): SelectOption[] {
    return figures.map((f) => ({ label: `${f.figureCode} - ${f.figureDescription}`, value: f.figureCode }));
}

/**
 * Map API section objects to Typeahead-compatible select options.
 *
 * @param sections - the sections returned by the API
 * @returns an array of select options
 * @example
 * ```ts
 * const options = mapSectionsToOptions([{ sectionName: 'Body', sectionNumber: 1 }]);
 * // [{ label: 'Body', value: '1' }]
 * ```
 */
function mapSectionsToOptions(sections: FailCodeSectionEntity[]): SelectOption[] {
    return sections.map((s) => ({ label: s.sectionName, value: s.sectionNumber.toString() }));
}

/**
 * Map API subsection objects to Typeahead-compatible select options.
 *
 * @param subsections - the subsections returned by the API
 * @returns an array of select options
 * @example
 * ```ts
 * const options = mapSubsectionsToOptions([{ subsectionName: 'Front Door', subsectionNumber: 2 }]);
 * // [{ label: 'Front Door', value: '2' }]
 * ```
 */
function mapSubsectionsToOptions(subsections: FailCodeSubsectionEntity[]): SelectOption[] {
    return subsections.map((s) => ({ label: s.subsectionName, value: s.subsectionNumber.toString() }));
}
